import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/auth-context';
import HopplaLogo from '../ui/HopplaLogo';
import { Button } from '../ui/Button';
import { canReadAudit } from '../../lib/permissions';
import { AdminSidebar } from './AdminSidebar';

const sectionNames: Record<string, string> = {
  clients: 'Clients',
  'web-projects': 'Projets Web',
  inquiries: 'Demandes Web',
  contacts: 'Contacts',
  operations: 'Surveillance',
  showcase: 'Vitrine du site',
  'showcase-home': 'Vitrine Accueil',
  audit: 'Journal d’audit',
};

export function PortalAdminShell(): JSX.Element {
  const location = useLocation();
  const { admin, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const section = sectionNames[location.pathname.split('/')[1] || ''] || 'Vue d’ensemble';
  const canOperate = admin?.role === 'OPS_ADMIN' || admin?.role === 'SUPER_ADMIN';

  useEffect(() => setIsMobileMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isMobileMenuOpen]);

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-main-column">
        <header className="admin-topbar">
          <div className="admin-topbar-left"><span className="admin-topbar-kicker">Bulle /</span><strong>{section}</strong></div>
          <div className="admin-topbar-right">
            <span className="admin-topbar-session-name" title={admin?.username ?? undefined}>{admin?.username ?? 'Admin'}</span>
            <Button variant="ghost" className="admin-topbar-logout" onClick={() => void logout()}>Déconnexion</Button>
          </div>
        </header>
        <header className="admin-mobile-header">
          <Link to="/" className="admin-mobile-header__brand" aria-label="Accueil Bulle Hoppla">
            <img src="/icons/hoppla-favicon-192.png" alt="" className="admin-mobile-header__mark" />
            <span className="admin-mobile-header__brand-copy"><HopplaLogo className="admin-mobile-header__logo" /><span>Bulle · {section}</span></span>
          </Link>
          <button type="button" className="admin-mobile-header__menu" aria-expanded={isMobileMenuOpen} aria-controls="portal-mobile-navigation" onClick={() => setIsMobileMenuOpen((open) => !open)}>{isMobileMenuOpen ? 'Fermer' : 'Menu'}</button>
        </header>
        {isMobileMenuOpen && (
          <nav id="portal-mobile-navigation" className="admin-mobile-drawer" aria-label="Navigation Bulle">
            {[
              { to: '/', label: 'Vue d’ensemble' },
              { to: '/clients', label: 'Clients' },
              { to: '/web-projects', label: 'Projets Web' },
              ...(canOperate ? [{ to: '/showcase', label: 'Vitrine du site' }, { to: '/showcase-home', label: 'Vitrine Accueil' }, { to: '/inquiries', label: 'Demandes Web' }, { to: '/contacts', label: 'Contacts' }, { to: '/operations', label: 'Surveillance' }] : []),
              ...(canReadAudit(admin?.role) ? [{ to: '/audit', label: 'Journal d’audit' }] : []),
            ].map((item) => {
              const isActive = item.to === '/' ? location.pathname === '/' : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
              return <Link key={item.to} to={item.to} aria-current={isActive ? 'page' : undefined}>{item.label}</Link>;
            })}
            <Button variant="ghost" onClick={() => void logout()}>Déconnexion</Button>
          </nav>
        )}
        <main className="admin-content"><Outlet /></main>
      </div>
    </div>
  );
}

export default PortalAdminShell;
